import React from 'react';
import { PaymentElement, useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import styles from './StripePaymentElement.module.scss';
import fetcher from 'utils/fetcher';
import SmallPreLoader from 'components/ui/SmallPreLoader/SmallPreLoader';
import type { SetupIntent, SetupIntentResult } from '@stripe/stripe-js';
import clsx from 'clsx';
import { UseStateProps } from 'types/generic';
import { paymentsFetch, addCardToWalkinRequestCrad } from 'store/actions/paymentMethods';
import { useDispatch } from 'react-redux';

export type Result = SetupIntentResult;

type StripePaymentFormProps = {
  mutate?: () => void;
  setOpen: UseStateProps<boolean>[1];
  rid?: string | number;
};

type StripeCheckoutFormProps = {
  secret: string;
  mutate?: () => void;
  setOpen: UseStateProps<boolean>[1];
  rid?: string | number;
};

export function usePaymentStatus({ clientSecret }: { clientSecret: string }) {
  const stripe = useStripe();
  const [message, setMessage] = React.useState('');

  React.useEffect(() => {
    if (!stripe) {
      return;
    }

    // Retrieve the "setup_intent_client_secret" query parameter appended to
    // your return_url by Stripe.js
    // Retrieve the SetupIntent
    stripe.retrieveSetupIntent(clientSecret).then(({ setupIntent }) => {
      // Inspect the SetupIntent `status` to indicate the status of the payment
      // to your customer.
      //
      // Some payment methods will [immediately succeed or fail][0] upon
      // confirmation, while others will first enter a `processing` state.
      //
      // [0]: https://stripe.com/docs/payments/payment-methods#payment-notification
      if (setupIntent && setupIntent.status)
        switch (setupIntent.status) {
          case 'succeeded':
            setMessage('Success! Your payment method has been saved.');
            break;
          case 'processing':
            setMessage("Processing payment details. We'll update you when processing is complete.");
            break;
          case 'requires_payment_method':
            // Redirect your user back to your payment page to attempt collecting
            // payment again
            setMessage('Failed to process payment details. Please try another payment method.');
            break;
        }
    });
  }, [clientSecret, stripe]);

  return message ? <p>{message}</p> : <SmallPreLoader />;
}

export function CheckoutForm({ secret, mutate, setOpen, rid }: Readonly<StripeCheckoutFormProps>) { 
  const stripe = useStripe();
  const elements = useElements();

  const [mount, setMount] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [paymentStatus, setPaymentStatus] = React.useState('incomplete');
  const [succeeded, setSucceeded] = React.useState(false);
  const dispatch = useDispatch();

  React.useEffect(() => {
    setMount(true);
    return () => setMount(false);
  }, [elements, mount, secret, stripe]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    if (!stripe || !elements) {
      setIsLoading(false);
      return;
    }

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        // Make sure to change this to your payment completion page
        return_url: 'http://localhost:3000/requestor/record-request/payment',
      },
      redirect: 'if_required',
    });

    setIsLoading(false);
    if (error?.message) setMessage(error.message);
    if (setupIntent && setupIntent.status === 'succeeded') {
      setSucceeded(true);
      mutate && mutate();
      setSucceeded(true);
      setPaymentStatus('succeeded');
      dispatch(paymentsFetch());
      if(rid) dispatch(addCardToWalkinRequestCrad({
        card_id: setupIntent.payment_method,
        request_id: rid
      }))

      setTimeout(() => {
        setOpen(false);
      }, 4000);

      setTimeout(() => {
        setSucceeded(false);
        setPaymentStatus('incomplete');
      }, 5000);
    }
  };

  return (
    <>
      {!succeeded && paymentStatus === 'incomplete' ? (
        <form id="payment-form" onSubmit={handleSubmit}>
          <PaymentElement id="payment-element" />
          <button
            className={clsx(styles.stripeButtonElement, 'px-3', styles['primary'], {
              [styles['is-disabled']]: isLoading || !(stripe || elements),
            })}
            disabled={isLoading || !(stripe || elements)}
            id="submit"
          >
            <span id="button-text">{isLoading ? <SmallPreLoader isDisabled={true} /> : 'Add Card'}</span>
          </button>

          {/* Show any error or success messages */}
          {message && <div id="payment-message">{message}</div>}
        </form>
      ) : null}

      <div className={clsx(styles['paymentAddedBanner'], !succeeded ? styles['paymentAddedBannerInvisible'] : '')}>
        <h1>Payment Method was added Successfully</h1>
      </div>
    </>
  );
}

export default function StripePaymentForm({ mutate, setOpen, rid }: StripePaymentFormProps) {
  const [mount, setMount] = React.useState(false);
  const [clientSecret, setClientSecret] = React.useState('');

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_TOKEN || '');

  React.useEffect(() => {
    async function setupPaymentIntent() {
      const { data, error } = await fetcher<{ intent: SetupIntent }>(`/api/v2/patient/cards/setup_payment_intent?request_id=${rid}`);
      if (!error && data && data?.intent?.client_secret) setClientSecret(data.intent.client_secret);
    }

    if (mount) setupPaymentIntent();

    setMount(true);
    return () => setMount(false);
  }, [mount]);

  return (
    <div className={styles.paymentContainer}>
      <div className={styles.paymentForm}>
        {!mount || !clientSecret ? (
          <SmallPreLoader isDisabled={true} />
        ) : (
          <Elements
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe' as const,
              },
            }}
            stripe={stripePromise}
          >
             <CheckoutForm setOpen={setOpen} mutate={mutate} secret={clientSecret} rid={rid}/>
          </Elements>
        )}
      </div>
    </div>
  );
}
